import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChefHat, Utensils, MapPin, CalendarClock, ShieldCheck, Megaphone, Users } from "lucide-react";
import Reveal from "../components/Reveal";

const highlights = [
  { icon: ChefHat, text: "Home chef onboarding and profile management" },
  { icon: MapPin, text: "Hyperlocal delivery with freshness guarantee" },
  { icon: CalendarClock, text: "Subscription and pre-order meal plans" },
  { icon: ShieldCheck, text: "FSSAI compliance support for home chefs" },
  { icon: Megaphone, text: "OOH-powered local discovery campaigns" },
  { icon: Users, text: "Community chef events and pop-ups" },
];

const heroImg = "https://images.unsplash.com/photo-1528712306091-ed0763094c98";
const dishImg = "https://images.pexels.com/photos/36299859/pexels-photo-36299859.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const Home2Plate = () => {
  return (
    <main>
      {/* HERO */}
      <section className="relative overflow-hidden" style={{ minHeight: "78vh", display: "flex", alignItems: "center" }}>
        <div className="absolute inset-0">
          <img src={heroImg} alt="Home cooking" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,11,13,0.72) 0%, rgba(11,11,13,0.92) 100%)" }} />
        </div>
        <div className="oo-container relative" style={{ paddingTop: 120, paddingBottom: 60 }}>
          <Reveal><span className="oo-eyebrow">Projects → Home2Plate</span></Reveal>
          <Reveal delay={80}>
            <h1 className="font-display font-extrabold" style={{ fontSize: "clamp(40px,7vw,84px)", lineHeight: 1.02, marginTop: 20, letterSpacing: "-0.02em" }}>
              Home<span className="text-accent">2</span>Plate
            </h1>
          </Reveal>
          <Reveal delay={140}>
            <p style={{ color: "var(--oo-muted)", fontSize: 20, marginTop: 18, fontWeight: 500 }}>Connecting Home Kitchens to Hungry Tables</p>
          </Reveal>
          <Reveal delay={200}>
            <p style={{ color: "var(--oo-muted)", fontSize: 16, lineHeight: 1.7, maxWidth: 640, marginTop: 20 }}>
              Home2Plate is OOHMA's food-tech venture that connects home chefs and cloud kitchens with customers seeking authentic, home-cooked meals. The platform empowers home cooks — particularly homemakers — to monetise their culinary skills with zero upfront infrastructure cost.
            </p>
          </Reveal>
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section className="oo-section">
        <div className="oo-container">
          <Reveal>
            <h2 className="font-display font-extrabold" style={{ fontSize: "clamp(28px,4vw,44px)", letterSpacing: "-0.02em" }}>Platform <span className="text-accent">Highlights</span></h2>
          </Reveal>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3" style={{ marginTop: 44 }}>
            {highlights.map((h, i) => {
              const Icon = h.icon;
              return (
                <Reveal key={h.text} delay={(i % 3) * 80}>
                  <div className="oo-card h-full flex items-start gap-4" style={{ padding: 28 }}>
                    <div className="inline-flex items-center justify-center shrink-0" style={{ width: 46, height: 46, borderRadius: 12, background: "var(--oo-orange-soft)" }}>
                      <Icon size={20} className="text-accent" />
                    </div>
                    <p style={{ color: "var(--oo-text)", lineHeight: 1.6, fontSize: 15, fontWeight: 500 }}>{h.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* TWO AUDIENCES */}
      <section className="oo-section" style={{ background: "var(--oo-bg-2)" }}>
        <div className="oo-container">
          <div className="grid gap-6 md:grid-cols-2">
            <Reveal>
              <div className="oo-card h-full" style={{ padding: 40 }}>
                <div className="inline-flex items-center justify-center" style={{ width: 56, height: 56, borderRadius: 16, background: "var(--oo-orange-soft)" }}>
                  <ChefHat size={26} className="text-accent" />
                </div>
                <h3 className="font-display" style={{ fontSize: 24, fontWeight: 700, marginTop: 22 }}>For Home Chefs</h3>
                <p style={{ color: "var(--oo-muted)", lineHeight: 1.75, marginTop: 14, fontSize: 15 }}>
                  Zero-cost entry. Get a digital storefront, manage orders, and deliver to your neighbourhood. OOHMA's local OOH network amplifies your reach within your community.
                </p>
              </div>
            </Reveal>
            <Reveal delay={100}>
              <div className="oo-card h-full" style={{ padding: 40 }}>
                <div className="inline-flex items-center justify-center" style={{ width: 56, height: 56, borderRadius: 16, background: "var(--oo-orange-soft)" }}>
                  <Utensils size={26} className="text-accent" />
                </div>
                <h3 className="font-display" style={{ fontSize: 24, fontWeight: 700, marginTop: 22 }}>For Customers</h3>
                <p style={{ color: "var(--oo-muted)", lineHeight: 1.75, marginTop: 14, fontSize: 15 }}>
                  Discover verified home chefs near you. Order fresh, authentic, home-cooked meals with transparent ingredients. Support local kitchens, not just restaurants.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ADVANTAGE */}
      <section className="oo-section">
        <div className="oo-container">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <Reveal delay={100} className="order-2 lg:order-1">
              <div>
                <h2 className="font-display font-extrabold" style={{ fontSize: "clamp(28px,4vw,44px)", letterSpacing: "-0.02em" }}>The OOHMA <span className="text-accent">Advantage</span></h2>
                <p style={{ color: "var(--oo-muted)", fontSize: 16, lineHeight: 1.8, marginTop: 22 }}>
                  Home2Plate uniquely leverages OOHMA's existing media assets — school screens, neighbourhood bus shelters, and local LED boards — to market home chefs hyperlocally with virtually zero digital ad spend. It's the only food platform where discovery happens offline first.
                </p>
              </div>
            </Reveal>
            <Reveal className="order-1 lg:order-2">
              <div style={{ overflow: "hidden", borderRadius: 24, border: "1px solid var(--oo-border)" }}>
                <img src={dishImg} alt="Home cooked meal" className="w-full object-cover" style={{ aspectRatio: "4/3" }} />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ paddingBottom: 110 }}>
        <div className="oo-container text-center">
          <Reveal>
            <h2 className="font-display font-extrabold" style={{ fontSize: "clamp(30px,4.5vw,52px)", letterSpacing: "-0.02em" }}>Partner With <span className="text-accent">Home2Plate</span></h2>
            <Link to="/contact" className="btn-primary" style={{ marginTop: 30 }}>Get in Touch <ArrowUpRight size={18} /></Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Home2Plate;
